import axios, { AxiosError } from 'axios';
import { startAuthentication } from '@simplewebauthn/browser';
import type { Patient } from '@bloom/shared';
import { analyticsHeaders } from './analytics';

/**
 * The top-level patient login (backend routes/dtp/login.ts).
 *
 * Its own client on purpose: dtpApi logs the patient out on any 401, and here a
 * 401 is the normal answer to a wrong code or password.
 */

export interface ClinicChoice {
  physicianId: string;
  clinicName: string;
  patientFirstName: string;
}

export interface SignedIn {
  identityToken: string;
  clinics: ClinicChoice[];
}

export interface ClinicSession {
  token: string;
  patient: Patient;
  physicianId: string;
}

const client = axios.create({
  baseURL: `${import.meta.env.VITE_API_URL || '/api'}/dtp/login`,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' }
});
// Ties the server's login_succeeded / login_failed to this visit's analytics session.
client.interceptors.request.use(config => {
  Object.assign(config.headers, analyticsHeaders());
  return config;
});

/** The server's message for a failed call, or a generic one. */
export function loginErrorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  const data = (err as AxiosError<{ error?: string }>)?.response?.data;
  return data?.error || fallback;
}

async function post<T>(url: string, body: object = {}): Promise<T> {
  const { data } = await client.post(url, body);
  return data.data as T;
}

export const patientLoginApi = {
  requestCode: (phone: string) => client.post('/code/request', { phone }),
  verifyCode: (phone: string, code: string) => post<SignedIn>('/code/verify', { phone, code }),
  requestLink: (email: string) => client.post('/link/request', { email }),
  verifyLink: (token: string) => post<SignedIn>('/link/verify', { token }),
  password: (phone: string, password: string) => post<SignedIn>('/password', { phone, password }),
  async passkey(): Promise<SignedIn> {
    const { challengeId, options } = await post<{ challengeId: string; options: any }>('/passkey/options');
    const response = await startAuthentication({ optionsJSON: options });
    return post<SignedIn>('/passkey/verify', { challengeId, response });
  },
  enter: (identityToken: string, physicianId: string) => post<ClinicSession>('/enter', { identityToken, physicianId })
};
